import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { axiosInstance } from "../App";
import { Building2, Users, FolderKanban, CheckSquare, LogOut, Plus, Loader2, Mail, Phone, MapPin, Edit, Trash2, Eye, UserCog } from "lucide-react";
import { toast } from "sonner";

function SupervisorPanel() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [companies, setCompanies] = useState([]);
  const [companyAdmins, setCompanyAdmins] = useState({}); // Store admins by company_id
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [editCompanyDialogOpen, setEditCompanyDialogOpen] = useState(false);
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: ""
  });
  const [editCompanyForm, setEditCompanyForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: ""
  });
  const [adminForm, setAdminForm] = useState({
    name: "",
    email: "",
    password: ""
  });
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({
    name: "",
    email: "",
    role: ""
  });
  const [testEmailDialogOpen, setTestEmailDialogOpen] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState("");
  const [testEmailCompany, setTestEmailCompany] = useState(null);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  useEffect(() => {
    // Check if user is supervisor
    const userData = JSON.parse(localStorage.getItem("user") || "{}");
    if (userData.role !== "supervisor") {
      toast.error(t('noAccessToPage'));
      navigate("/dashboard");
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, companiesRes] = await Promise.all([
        axiosInstance.get("/supervisor/stats"),
        axiosInstance.get("/supervisor/companies")
      ]);
      setStats(statsRes.data);
      setCompanies(companiesRes.data);
      
      // Fetch admins for each company
      const adminsMap = {};
      for (const company of companiesRes.data) {
        try {
          const usersRes = await axiosInstance.get(`/supervisor/companies/${company.id}/users`);
          adminsMap[company.id] = usersRes.data.filter(user => user.role === "admin");
        } catch (error) {
          adminsMap[company.id] = [];
        }
      }
      setCompanyAdmins(adminsMap);
    } catch (error) {
      toast.error(t('dataLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post("/supervisor/companies", companyForm);
      toast.success(t('companyCreated'));
      setCompanyDialogOpen(false);
      setCompanyForm({ name: "", email: "", phone: "", address: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('companyCreationFailed'));
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post(`/supervisor/companies/${selectedCompany.id}/admin`, null, {
        params: {
          name: adminForm.name,
          email: adminForm.email,
          password: adminForm.password
        }
      });
      toast.success(t('adminUserCreated'));
      setAdminDialogOpen(false);
      setAdminForm({ name: "", email: "", password: "" });
      setSelectedCompany(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('adminCreationFailed'));
    }
  };

  const handleToggleCompanyStatus = async (company) => {
    try {
      await axiosInstance.put(`/supervisor/companies/${company.id}`, {
        is_active: !company.is_active
      });
      toast.success(company.is_active ? t('companyDeactivated') : t('companyActivated'));
      fetchData();
    } catch (error) {
      toast.error(t('operationFailed'));
    }
  };

  const handleEditCompany = (company) => {
    setSelectedCompany(company);
    setEditCompanyForm({
      name: company.name,
      email: company.email,
      phone: company.phone || "",
      address: company.address || ""
    });
    setEditCompanyDialogOpen(true);
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.put(`/supervisor/companies/${selectedCompany.id}`, editCompanyForm);
      toast.success(t('companyUpdated'));
      setEditCompanyDialogOpen(false);
      setSelectedCompany(null);
      setEditCompanyForm({ name: "", email: "", phone: "", address: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('companyUpdateFailed'));
    }
  };

  const handleDeleteCompany = async (company) => {
    if (!window.confirm(t('confirmDeleteCompany', { companyName: company.name }))) {
      return;
    }
    
    try {
      const response = await axiosInstance.delete(`/supervisor/companies/${company.id}`);
      toast.success(t('companyDeletedSummary', { 
        users: response.data.deleted_users, 
        projects: response.data.deleted_projects, 
        tasks: response.data.deleted_tasks 
      }));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('companyDeleteFailed'));
    }
  };

  const handleViewUsers = async (company) => {
    setSelectedCompany(company);
    try {
      const response = await axiosInstance.get(`/supervisor/companies/${company.id}/users`);
      setCompanyUsers(response.data);
      setUsersDialogOpen(true);
    } catch (error) {
      toast.error(t('usersLoadFailed'));
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditUserForm({
      name: user.name,
      email: user.email,
      role: user.role
    });
    setEditUserDialogOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.put(`/supervisor/users/${selectedUser.id}`, null, {
        params: editUserForm
      });
      toast.success(t('userUpdated'));
      setEditUserDialogOpen(false);
      setSelectedUser(null);
      setEditUserForm({ name: "", email: "", role: "" });
      // Refresh data to update admin lists
      fetchData();
      // If users dialog is open, refresh it too
      if (usersDialogOpen && selectedCompany) {
        handleViewUsers(selectedCompany);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('userUpdateFailed'));
    }
  };

  const handleTestEmail = async (e) => {
    e.preventDefault();
    if (!testEmailAddress || !testEmailCompany) {
      toast.error("Lütfen e-posta adresi girin");
      return;
    }

    setSendingTestEmail(true);
    try {
      const response = await axiosInstance.post(
        `/supervisor/companies/${testEmailCompany.id}/test-email`,
        null,
        {
          params: { test_email: testEmailAddress }
        }
      );
      toast.success(response.data.message || "Test e-postası başarıyla gönderildi!");
      setTestEmailDialogOpen(false);
      setTestEmailAddress("");
      setTestEmailCompany(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Test e-postası gönderilemedi");
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleOpenTestEmail = (company) => {
    setTestEmailCompany(company);
    setTestEmailAddress("");
    setTestEmailDialogOpen(true);
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(t('confirmDeleteUser', { userName: user.name }))) {
      return;
    }
    
    try {
      await axiosInstance.delete(`/supervisor/users/${user.id}`);
      toast.success(t('userDeleted'));
      // Refresh data to update admin lists
      fetchData();
      // If users dialog is open, refresh it too
      if (usersDialogOpen && selectedCompany) {
        handleViewUsers(selectedCompany);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('cannotDeleteUser'));
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { label: t('adminRole'), color: "bg-red-100 text-red-800" },
      manager: { label: t('managerRole'), color: "bg-blue-100 text-blue-800" },
      engineer: { label: t('engineerRole'), color: "bg-purple-100 text-purple-800" },
      personnel: { label: t('personnelRole'), color: "bg-gray-100 text-gray-800" },
      member: { label: t('personnelRole'), color: "bg-gray-100 text-gray-800" }  // Backward compatibility
    };
    const config = roleConfig[role] || { label: role, color: "bg-gray-100 text-gray-800" };
    return (
      <span className={`px-2 py-1 text-xs rounded ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F8F9FA' }}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/milestora-logo-light.png" 
                alt="Milestora" 
                className="h-20 w-auto object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope', color: '#1F2937' }}>
                  Süpervizör Paneli
                </h1>
                <p className="text-sm text-gray-500 mt-1">Firma ve sistem yönetimi</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => navigate("/active-users")}>
                <Users className="h-4 w-4 mr-2" />
                Aktif Kullanıcılar
              </Button>
              <Button variant="outline" onClick={() => navigate("/activity-logs")}>
                <UserCog className="h-4 w-4 mr-2" />
                Aktivite Logları
              </Button>
              <Button variant="outline" onClick={() => navigate("/supervisor-smtp-settings")}>
                <Mail className="h-4 w-4 mr-2" />
                SMTP Ayarları
              </Button>
              <Button variant="outline" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Çıkış Yap
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Toplam Firma</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-blue-500" />
                <div className="text-2xl font-bold">{stats.total_companies || 0}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Aktif Firma</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-green-500" />
                <div className="text-2xl font-bold">{stats.active_companies || 0}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Toplam Kullanıcı</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-purple-500" />
                <div className="text-2xl font-bold">{stats.total_users || 0}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Toplam Proje</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <FolderKanban className="h-4 w-4 text-orange-500" />
                <div className="text-2xl font-bold">{stats.total_projects || 0}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Toplam Görev</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <CheckSquare className="h-4 w-4 text-pink-500" />
                <div className="text-2xl font-bold">{stats.total_tasks || 0}</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Companies */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Firmalar</CardTitle>
                <CardDescription>Sistemdeki tüm firmalar</CardDescription>
              </div>
              <Button onClick={() => setCompanyDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Yeni Firma
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {companies.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Henüz firma eklenmemiş
                </div>
              ) : (
                companies.map((company) => {
                  const admins = companyAdmins[company.id] || [];
                  return (
                  <div key={company.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-lg">{company.name}</h3>
                          {company.is_active ? (
                            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">Aktif</span>
                          ) : (
                            <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded">Pasif</span>
                          )}
                        </div>
                        <div className="space-y-1 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4" />
                            {company.email}
                          </div>
                          {company.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4" />
                              {company.phone}
                            </div>
                          )}
                          {company.address && (
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4" />
                              {company.address}
                            </div>
                          )}
                        </div>
                        
                        {/* Admin Users Section */}
                        {admins.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <div className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1">
                              <UserCog className="h-3 w-3" />
                              Admin Kullanıcılar ({admins.length})
                            </div>
                            <div className="space-y-2">
                              {admins.map((admin) => (
                                <div key={admin.id} className="flex items-center justify-between bg-gray-50 rounded p-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-gray-900 truncate">{admin.name}</div>
                                    <div className="text-xs text-gray-500 truncate">{admin.email}</div>
                                  </div>
                                  <div className="flex gap-1 ml-2">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 w-7 p-0"
                                      onClick={() => handleEditUser(admin)}
                                    >
                                      <Edit className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => handleDeleteUser(admin)}
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewUsers(company)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Kullanıcılar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedCompany(company);
                            setAdminDialogOpen(true);
                          }}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Admin
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenTestEmail(company)}
                          title="SMTP ayarlarını test et"
                        >
                          <Mail className="h-4 w-4 mr-1" />
                          Test Mail
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditCompany(company)}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Düzenle
                        </Button>
                        <Button
                          size="sm"
                          variant={company.is_active ? "secondary" : "default"}
                          onClick={() => handleToggleCompanyStatus(company)}
                        >
                          {company.is_active ? "Pasif" : "Aktif"}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteCompany(company)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Create Company Dialog */}
      <Dialog open={companyDialogOpen} onOpenChange={setCompanyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Yeni Firma Oluştur</DialogTitle>
            <DialogDescription>Sisteme yeni bir firma ekleyin</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateCompany} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="company-name">Firma Adı *</Label>
              <Input
                id="company-name"
                value={companyForm.name}
                onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                required
                placeholder="Örnek Yazılım Ltd."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company-email">Email *</Label>
              <Input
                id="company-email"
                type="email"
                value={companyForm.email}
                onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })}
                required
                placeholder="info@ornek.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company-phone">Telefon</Label>
              <Input
                id="company-phone"
                value={companyForm.phone}
                onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                placeholder="+90 555 123 4567"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company-address">Adres</Label>
              <Input
                id="company-address"
                value={companyForm.address}
                onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })}
                placeholder="İstanbul, Türkiye"
              />
            </div>
            <Button type="submit" className="w-full">
              Firma Oluştur
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog open={editCompanyDialogOpen} onOpenChange={setEditCompanyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Firma Düzenle</DialogTitle>
            <DialogDescription>Firma bilgilerini güncelleyin</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateCompany} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-company-name">Firma Adı *</Label>
              <Input
                id="edit-company-name"
                value={editCompanyForm.name}
                onChange={(e) => setEditCompanyForm({ ...editCompanyForm, name: e.target.value })}
                required
                placeholder="Örnek Yazılım Ltd."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-company-email">Email *</Label>
              <Input
                id="edit-company-email"
                type="email"
                value={editCompanyForm.email}
                onChange={(e) => setEditCompanyForm({ ...editCompanyForm, email: e.target.value })}
                required
                placeholder="info@ornek.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-company-phone">Telefon</Label>
              <Input
                id="edit-company-phone"
                value={editCompanyForm.phone}
                onChange={(e) => setEditCompanyForm({ ...editCompanyForm, phone: e.target.value })}
                placeholder="+90 555 123 4567"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-company-address">Adres</Label>
              <Input
                id="edit-company-address"
                value={editCompanyForm.address}
                onChange={(e) => setEditCompanyForm({ ...editCompanyForm, address: e.target.value })}
                placeholder="İstanbul, Türkiye"
              />
            </div>
            <Button type="submit" className="w-full">
              Firma Güncelle
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Admin Dialog */}
      <Dialog open={adminDialogOpen} onOpenChange={setAdminDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Firma Admin'i Oluştur</DialogTitle>
            <DialogDescription>
              {selectedCompany?.name} için admin kullanıcısı oluşturun
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateAdmin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="admin-name">Ad Soyad *</Label>
              <Input
                id="admin-name"
                value={adminForm.name}
                onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })}
                required
                placeholder="Ahmet Yılmaz"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-email">Email *</Label>
              <Input
                id="admin-email"
                type="email"
                value={adminForm.email}
                onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                required
                placeholder="admin@ornek.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password">Şifre *</Label>
              <Input
                id="admin-password"
                type="password"
                value={adminForm.password}
                onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                required
                placeholder="Güçlü bir şifre"
                minLength={6}
              />
            </div>
            <Button type="submit" className="w-full">
              Admin Kullanıcısı Oluştur
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Users Dialog */}
      <Dialog open={usersDialogOpen} onOpenChange={setUsersDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Firma Kullanıcıları</DialogTitle>
            <DialogDescription>
              {selectedCompany?.name} - Toplam {companyUsers.length} kullanıcı
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {companyUsers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Bu firmada henüz kullanıcı yok
              </div>
            ) : (
              companyUsers.map((user) => (
                <div key={user.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold">{user.name}</h4>
                        {getRoleBadge(user.role)}
                      </div>
                      <div className="text-sm text-gray-600 flex items-center gap-2">
                        <Mail className="h-3 w-3" />
                        {user.email}
                      </div>
                      <div className="text-xs text-gray-400">
                        Oluşturulma: {new Date(user.created_at).toLocaleString('tr-TR')}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditUser(user)}
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Düzenle
                      </Button>
                      {user.role !== "admin" && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteUser(user)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialogOpen} onOpenChange={setEditUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kullanıcı Düzenle</DialogTitle>
            <DialogDescription>
              Kullanıcı bilgilerini güncelleyin
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateUser} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-user-name">Ad Soyad *</Label>
              <Input
                id="edit-user-name"
                value={editUserForm.name}
                onChange={(e) => setEditUserForm({ ...editUserForm, name: e.target.value })}
                required
                placeholder="Ahmet Yılmaz"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-user-email">Email *</Label>
              <Input
                id="edit-user-email"
                type="email"
                value={editUserForm.email}
                onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
                required
                placeholder="user@ornek.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-user-role">Rol *</Label>
              <Select
                value={editUserForm.role}
                onValueChange={(value) => setEditUserForm({ ...editUserForm, role: value })}
              >
                <SelectTrigger id="edit-user-role">
                  <SelectValue placeholder="Rol seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Müdür</SelectItem>
                  <SelectItem value="engineer">Mühendis</SelectItem>
                  <SelectItem value="personnel">Personel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full">
              Kullanıcı Güncelle
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={testEmailDialogOpen} onOpenChange={setTestEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>SMTP Ayarlarını Test Et</DialogTitle>
            <DialogDescription>
              {testEmailCompany && `${testEmailCompany.name} firması için test e-postası gönderin`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleTestEmail} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="test-email">Test E-posta Adresi *</Label>
              <Input
                id="test-email"
                type="email"
                value={testEmailAddress}
                onChange={(e) => setTestEmailAddress(e.target.value)}
                required
                placeholder="test@example.com"
                disabled={sendingTestEmail}
              />
              <p className="text-xs text-gray-500">
                Test e-postası bu adrese gönderilecektir
              </p>
            </div>
            {testEmailCompany && testEmailCompany.smtp_settings?.smtp_server && (
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                <p className="font-semibold text-blue-900 mb-1">SMTP Ayarları:</p>
                <p className="text-blue-800">
                  Sunucu: {testEmailCompany.smtp_settings.smtp_server}:{testEmailCompany.smtp_settings.smtp_port}
                </p>
                <p className="text-blue-800">
                  Gönderen: {testEmailCompany.smtp_settings.smtp_username}
                </p>
              </div>
            )}
            {testEmailCompany && !testEmailCompany.smtp_settings?.smtp_server && (
              <div className="bg-orange-50 border border-orange-200 rounded p-3 text-sm">
                <p className="text-orange-800">
                  ⚠️ Bu firma için SMTP ayarları yapılmamış. Lütfen önce firma düzenle bölümünden SMTP ayarlarını yapın.
                </p>
              </div>
            )}
            <div className="flex gap-2">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => setTestEmailDialogOpen(false)}
                disabled={sendingTestEmail}
              >
                İptal
              </Button>
              <Button 
                type="submit" 
                className="flex-1"
                disabled={sendingTestEmail || !testEmailCompany?.smtp_settings?.smtp_server}
              >
                {sendingTestEmail ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Test E-postası Gönder
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SupervisorPanel;
